// /bank: the organisation's item bank, on the exam server (RFC-056 "The web
// UI"). Shown to a person the relay let in, as a bank admin or user; every
// call goes to the exam server with the relay's token (bank.ts). Laid out as
// the Admin page is (byj-1wv.51.18): the title and what the bank is, then its
// sections as tabs — each a path of its own, so a section is linked to — in
// the order a person uses them: a home of what to do next, the items, the
// papers; for an admin the blueprints they write, then the bank's keeping
// in two tabs, each two parts stacked under their headings as the Admin
// page's machines tab stacks its lists: what happened (jobs, the audit
// trail) and who and how (members, settings). An instructor does
// not write blueprints (RFC-056 §1): they choose one where they ask for a
// paper, and read a published one's page from there. An item, a blueprint
// and a paper each have a page under their section. Submitting a kit and
// asking for a paper are dialogs over the items and the papers; their own
// paths (/bank/submit, /bank/request) open the page with the dialog up.
import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { User } from "../api";
import { bank, type BankClient, type BankMe } from "../bank";
import { t } from "../i18n";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLead, PageNotice } from "../components/Page";
import { BankMembers } from "../components/BankMembers";
import { BankAudit } from "../components/BankAudit";
import { BankSettings } from "../components/BankSettings";
import { BankItemPage, BankItems, useItemTitles } from "../components/BankItems";
import { BankJobs } from "../components/BankJobs";
import { BankGuideButton } from "../components/BankGuide";
import { BankHome } from "../components/BankHome";
import { BankBlueprintPage, BankBlueprints } from "../components/BankBlueprints";
import { BankPaperPage, BankPapers } from "../components/BankPapers";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A section of the bank: its path, its name, and whether an admin's alone. */
type Section = { path: string; label: () => string; admin?: boolean };

export const sections: Section[] = [
  { path: "/bank", label: () => t("bank:navHome") },
  { path: "/bank/items", label: () => t("bank:navItems") },
  { path: "/bank/papers", label: () => t("bank:navPapers") },
  { path: "/bank/blueprints", label: () => t("bank:navBlueprints"), admin: true },
  { path: "/bank/activity", label: () => t("bank:navActivity"), admin: true },
  { path: "/bank/config", label: () => t("bank:navConfig"), admin: true },
];
const [home, items, papers, blueprints, activity, config] = sections as [Section, Section, Section, Section, Section, Section];

/** Each part of the keeping's two tabs, by the path it has kept: the tab it is in, and its heading's id there. */
export const keepingParts: Record<string, { tab: "activity" | "config"; id: string }> = {
  "/bank/jobs": { tab: "activity", id: "bank-part-jobs" },
  "/bank/audit": { tab: "activity", id: "bank-part-audit" },
  "/bank/members": { tab: "config", id: "bank-part-members" },
  "/bank/settings": { tab: "config", id: "bank-part-settings" },
};

/** What happened: the jobs and the audit trail, the names their targets are said by read once for both. */
function Activity({ client, path }: { client: BankClient; path: string }) {
  const names = useItemTitles(client, true);
  return (
    <>
      <Part id="bank-part-jobs" title={t("bank:navJobs")} focus={path === "/bank/jobs"}>
        <BankJobs client={client} names={names} />
      </Part>
      <Part id="bank-part-audit" title={t("bank:navAudit")} focus={path === "/bank/audit"}>
        <BankAudit client={client} names={names} />
      </Part>
    </>
  );
}

/** A part of the keeping under its heading, brought into view when its own path was asked for. */
function Part({ id, title, focus, children }: { id: string; title: string; focus: boolean; children: ReactNode }) {
  useEffect(() => {
    // a browser without it (a test's) keeps the page where it is
    if (focus) document.getElementById(id)?.scrollIntoView?.({ block: "start" });
  }, [focus, id]);
  return (
    <section className="bank-part" aria-labelledby={id}>
      <h2 id={id} className="bank-part-title">
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * The section a path is in: an item's page and submitting are the items',
 * a paper's page and asking for one the papers'; a blueprint's page is the
 * blueprints' to an admin, the papers' to an instructor, who chooses one
 * there; a part of the keeping is its tab's.
 */
export function sectionOf(path: string, admin: boolean): Section | undefined {
  if (path === "/bank") return home;
  if (path === "/bank/items" || path === "/bank/submit" || /^\/bank\/items\/[^/]+$/.test(path)) return items;
  if (path === "/bank/papers" || path === "/bank/request" || /^\/bank\/papers\/[^/]+$/.test(path)) return papers;
  if (path === "/bank/blueprints" || /^\/bank\/blueprints\/[^/]+$/.test(path)) return admin ? blueprints : papers;
  if (path === "/bank/activity" || keepingParts[path]?.tab === "activity") return activity;
  if (path === "/bank/config" || keepingParts[path]?.tab === "config") return config;
  return undefined;
}

/** The bank's sections as the app's tabs, each a link: the ones the person's role reaches. */
function BankTabs({ at, admin }: { at?: Section; admin: boolean }) {
  return (
    <nav className="tab-bar bank-tabs" aria-label={t("bank:navLabel")}>
      {sections
        .filter((s) => admin || !s.admin)
        .map((s) => (
          <Link key={s.path} to={s.path} className="tab-bar-tab" current={s === at}>
            {s.label()}
          </Link>
        ))}
    </nav>
  );
}

export function Bank({ path, me: user, client = bank, query }: { path: string; me: User; client?: BankClient; query?: URLSearchParams }) {
  const [me, setMe] = useState<BankMe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setMe(await client.me());
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client]);
  useEffect(() => {
    if (user.bank) void load();
  }, [load, user.bank]);

  if (!user.bank) {
    return (
      <PageNotice message={t("bank:notLetIn")} />
    );
  }
  const admin = (me?.role ?? user.bank.role) === "admin";
  const itemPage = /^\/bank\/items\/([^/]+)$/.exec(path);
  const bpPage = /^\/bank\/blueprints\/([^/]+)$/.exec(path);
  const paperPage = /^\/bank\/papers\/([^/]+)$/.exec(path);
  const section = sectionOf(path, admin);
  // an instructor at the blueprints' list is where they choose one: asking for a paper
  const asking = path === "/bank/request" || (path === "/bank/blueprints" && !admin);
  let body: ReactNode;
  if (error) body = <p className="error">{t("common:error", { m: error })}</p>;
  else if (!me) body = <p className="muted">{t("common:loading")}</p>;
  else if (!section) body = <p>{t("common:notFound")}</p>;
  else if (section.admin && !admin) body = <p className="empty-card muted small">{t("bank:adminsOnly")}</p>;
  else if (section === home) body = <BankHome client={client} me={me} />;
  else if (itemPage) body = <BankItemPage key={itemPage[1]} client={client} id={decodeURIComponent(itemPage[1]!)} admin={admin} />;
  else if (bpPage) body = <BankBlueprintPage key={bpPage[1]} client={client} me={me} id={decodeURIComponent(bpPage[1]!)} />;
  else if (paperPage) body = <BankPaperPage key={paperPage[1]} client={client} id={decodeURIComponent(paperPage[1]!)} />;
  else if (section === items)
    body = <BankItems client={client} me={me} submit={path === "/bank/submit" ? { item: query?.get("item") ?? undefined } : undefined} />;
  else if (section === papers) body = <BankPapers client={client} me={me} request={asking ? { blueprint: query?.get("blueprint") ?? undefined } : undefined} />;
  else if (section === blueprints) body = <BankBlueprints client={client} me={me} />;
  else if (section === activity) body = <Activity client={client} path={path} />;
  else
    body = (
      <>
        <Part id="bank-part-members" title={t("bank:navMembers")} focus={path === "/bank/members"}>
          <BankMembers client={client} />
        </Part>
        <Part id="bank-part-settings" title={t("bank:navSettings")} focus={path === "/bank/settings"}>
          <BankSettings client={client} />
        </Part>
      </>
    );
  return (
    <Page className="bank-page">
      <PageHead
        title={t("bank:title")}
        meta={
          <span className="chip" data-testid="bank-role">
            {admin ? t("bank:roleAdmin") : t("bank:roleUser")}
          </span>
        }
        actions={<BankGuideButton />}
      />
      <PageLead>{admin ? t("bank:homeAdmin") : t("bank:homeUser")}</PageLead>
      <BankTabs at={section} admin={admin} />
      <div className="rows-panel">{body}</div>
    </Page>
  );
}
